import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';

const DISMISSED_KEY = 'godmode_notification_banner_dismissed';

export function NotificationPermissionBanner() {
  const { isAuthenticated } = useAuthStore();
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  });

  useEffect(() => {
    // Re-check permission periodically (user may grant via browser settings)
    const interval = setInterval(() => {
      if (typeof Notification !== 'undefined') {
        setPermission(Notification.permission);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Don't show if: not authenticated, already granted, dismissed, or API not available
  if (!isAuthenticated || permission === 'granted' || dismissed || typeof Notification === 'undefined') {
    return null;
  }

  const handleEnable = async () => {
    const result = await Notification.requestPermission();
    setPermission(result);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
      <div className="flex items-center justify-between max-w-screen-xl mx-auto">
        <div className="flex items-center gap-2 text-sm text-amber-800">
          <Bell className="h-4 w-4 flex-shrink-0" />
          <span>
            {permission === 'denied'
              ? 'Notifications are blocked. Enable them in your browser settings to receive schedule reminders.'
              : 'Enable notifications to receive reminders for your scheduled tasks.'}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {permission === 'default' && (
            <button
              onClick={handleEnable}
              className="text-sm font-medium text-amber-900 bg-amber-200 hover:bg-amber-300 px-3 py-1 rounded-md transition-colors"
            >
              Enable
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="text-amber-600 hover:text-amber-800 p-1"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
